if (typeof AnnouncementBar !== 'function') {
	class AnnouncementBar extends HTMLElement {
		constructor() {
			super();

			this.slider = this.querySelector('[data-js-slider]');
			this.content = this.querySelectorAll('.announcement');
			this.nextButton = this.querySelector('.announcement-bar__content-nav--right');
			this.prevButton = this.querySelector('.announcement-bar__content-nav--left');
			this._autoplay = parseInt(this.getAttribute('autoplay'), 10) || 0;

			this.index = 0;
			this.length = this.content.length;
			this.autoplayInterval = null;

			this._onNext = () => {
				this.changeSlide('next');
				this._restartAutoplay();
			};

			this._onPrev = () => {
				this.changeSlide('prev');
				this._restartAutoplay();
			};

			this._onScroll = () => this._debounceScrollSync();
			this._onScrollEnd = () => this._syncIndexFromScroll(true);
			this._onTouchEnd = () => this._syncIndexFromScroll(true);
			this._onPointerUp = () => this._syncIndexFromScroll(true);

			if (this.nextButton) this.nextButton.addEventListener('click', this._onNext);
			if (this.prevButton) this.prevButton.addEventListener('click', this._onPrev);

			if (this.slider) {
				this.slider.addEventListener?.('scrollend', this._onScrollEnd, { passive: true });
				this.slider.addEventListener('scroll', this._onScroll, { passive: true });
				this.slider.addEventListener('touchend', this._onTouchEnd, { passive: true });
				this.slider.addEventListener('pointerup', this._onPointerUp, { passive: true });
			}

			this._updateNavState();

			if (this._autoplay > 0) {
				this.autoplay();
			}
		}

		disconnectedCallback() {
			if (this.nextButton) this.nextButton.removeEventListener('click', this._onNext);
			if (this.prevButton) this.prevButton.removeEventListener('click', this._onPrev);
			if (this.slider) {
				this.slider.removeEventListener?.('scrollend', this._onScrollEnd);
				this.slider.removeEventListener('scroll', this._onScroll);
				this.slider.removeEventListener('touchend', this._onTouchEnd);
				this.slider.removeEventListener('pointerup', this._onPointerUp);
			}
			if (this.autoplayInterval) clearInterval(this.autoplayInterval);
			if (this._scrollSyncTimer) clearTimeout(this._scrollSyncTimer);
		}

		_updateNavState() {
			if (this.nextButton) {
				this.nextButton.classList.toggle(
					'announcement-bar__content-nav--disabled',
					this.index >= this.length - 1
				);
			}
			if (this.prevButton) {
				this.prevButton.classList.toggle('announcement-bar__content-nav--disabled', this.index <= 0);
			}
		}

		_scrollToIndex(i) {
			const target = this.content[i];

			if (!this.slider || !target) return;

			this.slider.scrollTo({
				top: 0,
				left: target.offsetLeft,
				behavior: 'smooth',
			});
		}

		changeSlide(direction) {
			if (this.nextButton) this.nextButton.classList.remove('announcement-bar__content-nav--disabled');
			if (this.prevButton) this.prevButton.classList.remove('announcement-bar__content-nav--disabled');

			let nextIndex = this.index;
			if (direction === 'next') nextIndex += 1;
			if (direction === 'prev') nextIndex -= 1;

			nextIndex = Math.max(0, Math.min(nextIndex, this.length - 1));

			this.index = nextIndex;
			this._updateNavState();
			this._scrollToIndex(this.index);
		}

		autoplay() {
			if (this.autoplayInterval) clearInterval(this.autoplayInterval);

			this.autoplayInterval = setInterval(() => {
				if (this.index + 1 === this.length) {
					this.index = 0;
					this._updateNavState();
					this._scrollToIndex(this.index);
				} else {
					this.changeSlide('next');
				}
			}, this._autoplay);
		}

		_restartAutoplay() {
			if (this._autoplay > 0) {
				if (this.autoplayInterval) clearInterval(this.autoplayInterval);
				this.autoplay();
			}
		}

		_syncIndexFromScroll(shouldRestartAutoplay = false) {
			if (!this.slider || !this.content?.length) return;

			const scrollLeft = this.slider.scrollLeft;
			let closestIndex = 0;
			let minDist = Infinity;

			for (let i = 0; i < this.content.length; i++) {
				const el = this.content[i];
				const dist = Math.abs(el.offsetLeft - scrollLeft);
				if (dist < minDist) {
					minDist = dist;
					closestIndex = i;
				}
			}

			if (closestIndex !== this.index) {
				this.index = closestIndex;
				this._updateNavState();
			} else {
				this._updateNavState();
			}

			if (shouldRestartAutoplay) this._restartAutoplay();
		}

		_debounceScrollSync() {
			if (this._scrollSyncTimer) clearTimeout(this._scrollSyncTimer);

			this._scrollSyncTimer = setTimeout(() => this._syncIndexFromScroll(false), 80);
		}
	}

	if (typeof customElements.get('announcement-bar') === 'undefined') {
		customElements.define('announcement-bar', AnnouncementBar);
	}
}
